import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";

export const EASE = [0.22, 1, 0.36, 1];

export function Reveal({ children, delay = 0, y = 28, className = "", once = true }) {
  return (
    <motion.div
      initial={{ opacity: 0, y }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once, amount: 0.15 }}
      transition={{ duration: 0.9, delay, ease: EASE }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

export function MaskLines({ lines, className = "", lineClassName = "", delay = 0, onMount = false }) {
  const rootProps = onMount
    ? { animate: "visible" }
    : { whileInView: "visible", viewport: { once: true, amount: 0.2 } };
  return (
    <motion.span className={`block ${className}`} initial="hidden" {...rootProps}>
      {lines.map((line, i) => (
        <span key={i} className="block overflow-hidden pb-[0.09em] -mb-[0.09em]">
          <motion.span
            className={`block will-change-transform ${lineClassName}`}
            variants={{
              hidden: { y: "112%" },
              visible: {
                y: 0,
                transition: { duration: 1.1, ease: EASE, delay: delay + i * 0.13 },
              },
            }}
          >
            {line}
          </motion.span>
        </span>
      ))}
    </motion.span>
  );
}

export function Label({ children, light = false, className = "" }) {
  return (
    <Reveal y={16}>
      <p
        className={`text-[11px] uppercase tracking-[0.3em] font-medium ${
          light ? "text-paper/60" : "text-smoke"
        } ${className}`}
      >
        {children}
      </p>
    </Reveal>
  );
}

export function ImageReveal({ src, alt, className = "", imgClassName = "", delay = 0, testid }) {
  return (
    <motion.div
      className={`overflow-hidden ${className}`}
      initial={{ clipPath: "inset(6% 3% 6% 3%)" }}
      whileInView={{ clipPath: "inset(0% 0% 0% 0%)" }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 1.2, ease: EASE, delay }}
      data-testid={testid}
    >
      <motion.img
        src={src}
        alt={alt}
        loading="lazy"
        initial={{ scale: 1.12, opacity: 0.4 }}
        whileInView={{ scale: 1, opacity: 1 }}
        viewport={{ once: true, amount: 0.2 }}
        transition={{ duration: 1.5, ease: EASE, delay }}
        className={`h-full w-full object-cover ${imgClassName}`}
      />
    </motion.div>
  );
}

export function ParallaxImage({ src, alt, className = "", amount = 10, testid }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], [`-${amount}%`, `${amount}%`]);
  return (
    <div ref={ref} className={`relative overflow-hidden ${className}`} data-testid={testid}>
      <motion.img
        src={src}
        alt={alt}
        loading="lazy"
        style={{ y }}
        className="absolute inset-0 h-full w-full object-cover scale-[1.22]"
      />
    </div>
  );
}

export function Mark({ className = "" }) {
  return (
    <svg viewBox="0 0 32 32" className={className} aria-hidden="true">
      <circle
        cx="16"
        cy="16"
        r="11.5"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.4"
        strokeLinecap="round"
        strokeDasharray="58 14.2"
        transform="rotate(112 16 16)"
      />
      <circle cx="16" cy="16" r="2.4" fill="currentColor" />
    </svg>
  );
}
